import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import { useLocation } from 'react-router-dom'; // Import useLocation
// Import the API functions (adjust path as needed)
import {
    searchPlayers,
    saveManualTeam,
    getSleeperLeagues,
    getSleeperRosters,
    syncSleeperTeam,
    // --- Import Yahoo functions --- 
    redirectToYahooLogin,
    getYahooLeagues,
    getYahooUserTeamsInLeague,
    syncYahooTeam
} from '../api/fantasyteams'; // Adjust path to your api.js file

// Assume you have a PlayerBadge component
const PlayerBadge = ({ player, onRemove }) => (
      <div className="badge badge-lg badge-primary gap-1">
          {player.name} ({player.position})
          {/* Added aria-label for accessibility */}
          <button onClick={() => onRemove(player.id)} className="btn btn-ghost btn-circle btn-xs ml-1" aria-label={`Remove ${player.name}`}>✕</button>
      </div>
);


function AddTeamPage() {
    const [mode, setMode] = useState('manual');
    const location = useLocation(); // Get location object

    // --- Manual Mode State ---
    const [leagueName, setLeagueName] = useState('');
    const [teamName, setTeamName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedPlayers, setSelectedPlayers] = useState([]);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [isLoadingSave, setIsLoadingSave] = useState(false);
    const [manualError, setManualError] = useState(null);
    const [manualSuccess, setManualSuccess] = useState(null);

    // --- Sleeper Mode State ---
    const [sleeperUsername, setSleeperUsername] = useState('');
    const [sleeperLeagues, setSleeperLeagues] = useState([]);
    const [selectedSleeperLeagueId, setSelectedSleeperLeagueId] = useState('');
    const [sleeperUserRosters, setSleeperUserRosters] = useState([]);
    const [selectedSleeperRosterId, setSelectedSleeperRosterId] = useState('');
    const [isLoadingSleeperLeagues, setIsLoadingSleeperLeagues] = useState(false);
    const [isLoadingSleeperRosters, setIsLoadingSleeperRosters] = useState(false);
    const [isLoadingSync, setIsLoadingSync] = useState(false);
    const [sleeperError, setSleeperError] = useState(null);
    const [sleeperSuccess, setSleeperSuccess] = useState(null);

    // --- Yahoo Mode State --- 
    const [yahooLeagues, setYahooLeagues] = useState([]);
    const [selectedYahooLeagueKey, setSelectedYahooLeagueKey] = useState('');
    const [yahooTeamsInLeague, setYahooTeamsInLeague] = useState([]);
    const [selectedYahooTeamKey, setSelectedYahooTeamKey] = useState('');
    const [isLoadingYahooLeagues, setIsLoadingYahooLeagues] = useState(false);
    const [isLoadingYahooTeams, setIsLoadingYahooTeams] = useState(false);
    const [isLoadingYahooSync, setIsLoadingYahooSync] = useState(false);
    const [yahooError, setYahooError] = useState(null);
    const [yahooSuccess, setYahooSuccess] = useState(null);
    const [yahooAuthAttempted, setYahooAuthAttempted] = useState(false); // Track if login was tried


    // --- Check for Yahoo Auth Success on Load --- 
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('yahoo_auth') === 'success') {
            setMode('yahoo'); // Switch to Yahoo tab
            setYahooSuccess("Successfully connected to Yahoo! Fetching leagues...");
            handleFetchYahooLeagues(); // Automatically fetch leagues
            setYahooAuthAttempted(true);
            // Optional: Clean the URL query parameter
            // window.history.replaceState(null, '', location.pathname);
        } else if (params.get('yahoo_auth') === 'error') {
             setMode('yahoo');
             setYahooError("Failed to connect to Yahoo. Please try again.");
             setYahooAuthAttempted(true);
             // window.history.replaceState(null, '', location.pathname);
        }
    }, [location]); // Re-run if location changes


    // --- Player Search (Debounced using useEffect) ---
    useEffect(() => {
        const handleSearch = async () => {
            // Trim whitespace and check length
            const trimmedSearchTerm = searchTerm.trim();
            if (trimmedSearchTerm.length < 2) {
                setSearchResults([]);
                return;
            }

            setIsLoadingSearch(true);
            setManualError(null); // Clear previous errors
            try {
                // *** USE API CALL ***
                const results = await searchPlayers(trimmedSearchTerm);
                const selectedIds = new Set(selectedPlayers.map(p => p.id));
                // Ensure results is an array before filtering
                const validResults = Array.isArray(results) ? results : [];
                setSearchResults(validResults.filter(r => !selectedIds.has(r.id)));
            } catch (error) {
                console.error("Player search failed:", error);
                // Use error detail from API if available, otherwise generic message
                setManualError(error.data?.detail || error.message || "Failed to search for players.");
                setSearchResults([]);
            } finally {
                setIsLoadingSearch(false);
            }
        };

        // Debounce
        const timerId = setTimeout(() => {
            handleSearch();
        }, 500); // 500ms delay

        // Cleanup function to clear the timeout if searchTerm or selectedPlayers change before the delay is over
        return () => clearTimeout(timerId);

        // Dependencies for the effect: re-run search if searchTerm changes or if selectedPlayers change
        // Re-running when selectedPlayers change ensures search results update if a selected player was previously in the results list
    }, [searchTerm, selectedPlayers]); // Added selectedPlayers as a dependency


    // --- Add/Remove Players (Manual Mode) ---
    const addPlayer = useCallback((player) => {
        // Check if player is already in the selectedPlayers list
        if (!selectedPlayers.some(p => p.id === player.id)) {
            setSelectedPlayers(prevPlayers => [...prevPlayers, player]);
            // Clear search related state after adding a player
            setSearchTerm('');
            setSearchResults([]);
            // Optionally clear manual error/success if a player is added
            setManualError(null);
            setManualSuccess(null);
        }
    }, [selectedPlayers]); // Dependency on selectedPlayers


    const removePlayer = useCallback((playerId) => {
        // Filter out the player with the matching ID
        setSelectedPlayers(prevPlayers => prevPlayers.filter(p => p.id !== playerId));
        // Optionally clear manual error/success after removing a player
        setManualError(null);
        setManualSuccess(null);
        // No need to re-run search immediately here, the useEffect dependency handles it
    }, []); // No dependencies needed if only using playerId


    // --- Save Manual Team ---
    const handleSaveManualTeam = async () => {
        setManualError(null);
        setManualSuccess(null);
        // Trim inputs for validation
        const trimmedLeagueName = leagueName.trim();
        const trimmedTeamName = teamName.trim();

        if (!trimmedLeagueName || !trimmedTeamName || selectedPlayers.length === 0) {
            setManualError("Please provide league name, team name, and add at least one player.");
            return;
        }
        setIsLoadingSave(true);
        try {
            const playerIds = selectedPlayers.map(p => p.id);
            // *** USE API CALL ***
            const savedTeam = await saveManualTeam({ leagueName: trimmedLeagueName, teamName: trimmedTeamName, playerIds });
            console.log("Saved Manual Team:", savedTeam); // Log the response from backend
            setManualSuccess(`Team "${trimmedTeamName}" saved successfully!`);

            // Reset form on success
            setLeagueName('');
            setTeamName('');
            setSelectedPlayers([]);
            setSearchTerm('');
            setSearchResults([]); // Clear results as no players are selected anymore

        } catch (error) {
            console.error("Save manual team failed:", error);
            // Handle potential API error responses
            // The API error object might have a specific structure (e.g., { status: 401, data: { detail: "..." } })
            // Attempt to extract a meaningful message
            const errorMessage = error.data?.detail || error.message || "Failed to save team. Please try again.";

            // Handle specific HTTP status codes if your API provides them
            if (error.status === 401) {
                setManualError("Authentication failed. Please log in again.");
                // Optional: trigger redirect to login page (e.g., using React Router's history or navigate)
            } else if (error.status === 409) { // Conflict (e.g., duplicate team name)
                 setManualError(errorMessage); // Use the message from the API if available
            } else if (error.status === 400) { // Bad Request (e.g., invalid data)
                 setManualError(errorMessage);
            }
            else {
                setManualError(errorMessage);
            }
        } finally {
            setIsLoadingSave(false); // Turn off loading state
        }
    };

    // --- Fetch Sleeper Leagues ---
    const handleFetchSleeperLeagues = async () => {
        // Trim username input for validation and API call
        const trimmedSleeperUsername = sleeperUsername.trim();

        if (!trimmedSleeperUsername) {
            setSleeperError("Please enter your Sleeper username.");
            return;
        }
        setIsLoadingSleeperLeagues(true);
        setSleeperError(null); // Clear previous errors
        setSleeperSuccess(null);
        setSleeperLeagues([]); // Reset dependent state immediately
        setSelectedSleeperLeagueId('');
        setSleeperUserRosters([]);
        setSelectedSleeperRosterId('');

        try {
            const currentYear = new Date().getFullYear();
            // *** USE API CALL ***
            const leagues = await getSleeperLeagues(trimmedSleeperUsername, currentYear);

            // Ensure leagues is an array before processing
            const validLeagues = Array.isArray(leagues) ? leagues : [];

            setSleeperLeagues(validLeagues);

            if (validLeagues.length === 0) {
                setSleeperError(`No active leagues found for username "${trimmedSleeperUsername}" in ${currentYear}, or the username is incorrect.`);
            } else {
                 setSleeperSuccess(`${validLeagues.length} league(s) found.`); // Optional: show number of leagues found
            }

        } catch (error) {
            console.error("Fetch Sleeper leagues failed:", error);
            const errorMessage = error.data?.detail || error.message || "Failed to fetch Sleeper leagues.";
             // Handle specific HTTP status codes if your API provides them
            if (error.status === 404) { // User not found on Sleeper
                 setSleeperError(errorMessage);
            } else {
                 setSleeperError(errorMessage);
            }
        } finally {
            setIsLoadingSleeperLeagues(false); // Turn off loading state
        }
    };

    // --- Fetch Sleeper Rosters when League Changes (useEffect) ---
    useEffect(() => {
        const fetchRosters = async () => {
            // Check for both selected league ID and non-empty trimmed username before fetching rosters
            const trimmedSleeperUsername = sleeperUsername.trim();
            if (!selectedSleeperLeagueId || !trimmedSleeperUsername) {
                setSleeperUserRosters([]);
                setSelectedSleeperRosterId('');
                return;
            }

            setIsLoadingSleeperRosters(true);
            setSleeperError(null); // Clear errors specific to roster fetching
            // No need to clear sleeperSuccess here if fetching rosters after finding leagues

            try {
                // *** USE API CALL ***
                const rosters = await getSleeperRosters(selectedSleeperLeagueId, trimmedSleeperUsername);

                // Ensure rosters is an array before processing
                const validRosters = Array.isArray(rosters) ? rosters : [];

                setSleeperUserRosters(validRosters);

                if (validRosters.length > 0) {
                    if (validRosters.length === 1) {
                        setSelectedSleeperRosterId(validRosters[0].roster_id); // Auto-select if only one roster found for the user in this league
                        // Optionally set a success message indicating the team was found
                        // setSleeperSuccess(`Your team "${validRosters[0].team_name}" found.`);
                    } else {
                        setSelectedSleeperRosterId(''); // Require user selection if multiple rosters found
                        // Optional: set a message indicating multiple teams found
                        // setSleeperSuccess(`Found ${validRosters.length} teams in this league. Please select yours.`);
                    }
                } else {
                    setSleeperError("Could not find your roster(s) in the selected league.");
                    setSelectedSleeperRosterId(''); // Clear selection
                }

            } catch (error) {
                console.error("Fetch Sleeper rosters failed:", error);
                const errorMessage = error.data?.detail || error.message || "Failed to fetch your team(s) for the selected league.";
                setSleeperError(errorMessage);
                setSleeperUserRosters([]); // Clear rosters on error
                setSelectedSleeperRosterId(''); // Clear selection on error
            } finally {
                setIsLoadingSleeperRosters(false); // Turn off loading state
            }
        };

        fetchRosters(); // Call the async function defined within the effect

        // Dependencies for the effect: re-run if selectedSleeperLeagueId or sleeperUsername change
    }, [selectedSleeperLeagueId, sleeperUsername]); // Added sleeperUsername as a dependency


    // --- Sync Sleeper Team ---
    const handleSyncSleeperTeam = async () => {
        // Ensure both league and roster are selected before attempting to sync
        if (!selectedSleeperLeagueId || !selectedSleeperRosterId) {
            setSleeperError("Please select a league and your team to sync.");
            return;
        }

        setIsLoadingSync(true);
        setSleeperError(null); // Clear previous errors
        setSleeperSuccess(null); // Clear previous success messages

        try {
            // Find the selected league and roster data to get names for the API call (optional but good practice)
            const leagueData = sleeperLeagues.find(l => l.league_id === selectedSleeperLeagueId);
            const rosterData = sleeperUserRosters.find(r => r.roster_id === selectedSleeperRosterId);

            // *** USE API CALL ***
            const syncedTeam = await syncSleeperTeam({
                sleeperLeagueId: selectedSleeperLeagueId,
                sleeperRosterId: selectedSleeperRosterId,
                // Provide names to the API if available from the fetched data
                leagueName: leagueData?.name,
                teamName: rosterData?.team_name
            });

            console.log("Synced Sleeper Team:", syncedTeam); // Log the response from backend

            // Set success message using the synced team info if available, or fallback to selected names
            setSleeperSuccess(`Team "${syncedTeam?.team_name || rosterData?.team_name || 'Selected Team'}" synced successfully!`);

            // --- Reset Sleeper form state after successful sync ---
            setSleeperUsername(''); // Clear username input
            setSleeperLeagues([]); // Clear fetched leagues
            setSelectedSleeperLeagueId(''); // Clear selected league
            setSleeperUserRosters([]); // Clear fetched rosters
            setSelectedSleeperRosterId(''); // Clear selected roster
            // No need to clear success/error messages immediately after sync


        } catch (error) {
            console.error("Sync Sleeper team failed:", error);
            // Handle potential API error responses
            const errorMessage = error.data?.detail || error.message || "Failed to sync Sleeper team. Please try again.";

            // Handle specific HTTP status codes if your API provides them
            if (error.status === 401) {
                setSleeperError("Authentication failed. Please log in again.");
                // Optional: trigger redirect to login page
            } else if (error.status === 409) { // Conflict (e.g., already synced)
                 setSleeperError(errorMessage);
            } else if (error.status === 404) { // League or Roster not found (less likely if fetched successfully, but safety)
                 setSleeperError(errorMessage);
            } else if (error.status === 400) { // Bad Request (e.g., invalid data sent)
                 setSleeperError(errorMessage);
            }
            else {
                setSleeperError(errorMessage);
            }
        } finally {
            setIsLoadingSync(false); // Turn off loading state
        }
    };

    // --- NEW: Fetch Yahoo Leagues --- 
    const handleFetchYahooLeagues = useCallback(async () => {
        setIsLoadingYahooLeagues(true);
        setYahooError(null);
        setYahooSuccess(null);
        setYahooLeagues([]);
        setSelectedYahooLeagueKey('');
        setYahooTeamsInLeague([]);
        setSelectedYahooTeamKey('');
        try {
            const currentYear = new Date().getFullYear();
            const leagues = await getYahooLeagues(currentYear);
            const validLeagues = Array.isArray(leagues) ? leagues : [];
            setYahooLeagues(validLeagues);
            if (validLeagues.length === 0) {
                setYahooError(`No active leagues found for your linked Yahoo account in ${currentYear}. Ensure you have active fantasy football leagues.`);
            } else {
                setYahooSuccess(`${validLeagues.length} Yahoo league(s) found for ${currentYear}.`);
            }
        } catch (error) {
            console.error("Fetch Yahoo leagues failed:", error);
            const errorMessage = error.response?.data?.detail || error.message || "Failed to fetch Yahoo leagues.";
            if (error.response?.status === 401) {
                setYahooError("Yahoo authentication failed or expired. Please connect/reconnect your Yahoo account.");
            } else {
                setYahooError(errorMessage);
            }
        } finally {
            setIsLoadingYahooLeagues(false);
            setYahooAuthAttempted(true); // Mark that we tried after auth attempt
        }
    }, []); // No dependencies needed, relies on button click


    // --- NEW: Fetch Yahoo Teams in League --- 
    useEffect(() => {
        const fetchYahooTeams = async () => {
            if (!selectedYahooLeagueKey) {
                setYahooTeamsInLeague([]);
                setSelectedYahooTeamKey('');
                return;
            }
            setIsLoadingYahooTeams(true);
            setYahooError(null); // Clear team-specific error
            try {
                const teams = await getYahooUserTeamsInLeague(selectedYahooLeagueKey);
                const validTeams = Array.isArray(teams) ? teams : [];
                setYahooTeamsInLeague(validTeams);
                if (validTeams.length > 0) {
                    if (validTeams.length === 1) {
                        setSelectedYahooTeamKey(validTeams[0].team_key);
                    } else {
                        setSelectedYahooTeamKey(''); // Require selection for multiple teams
                    }
                } else {
                    setYahooError("Could not find your team(s) in the selected Yahoo league.");
                    setSelectedYahooTeamKey('');
                }
            } catch (error) {
                console.error("Fetch Yahoo teams failed:", error);
                const errorMessage = error.response?.data?.detail || error.message || "Failed to fetch your team(s) for the selected Yahoo league.";
                 if (error.response?.status === 401) {
                     setYahooError("Yahoo authentication failed or expired. Please reconnect.");
                 } else {
                     setYahooError(errorMessage);
                 }
                setYahooTeamsInLeague([]);
                setSelectedYahooTeamKey('');
            } finally {
                setIsLoadingYahooTeams(false);
            }
        };
        fetchYahooTeams();
    }, [selectedYahooLeagueKey]); // Dependency: run when league changes


    // --- NEW: Sync Yahoo Team --- 
    const handleSyncYahooTeam = async () => {
        if (!selectedYahooLeagueKey || !selectedYahooTeamKey) {
            setYahooError("Please select a Yahoo league and team to sync.");
            return;
        }
        setIsLoadingYahooSync(true);
        setYahooError(null);
        setYahooSuccess(null);
        try {
            const leagueData = yahooLeagues.find(l => l.league_key === selectedYahooLeagueKey);
            const teamData = yahooTeamsInLeague.find(t => t.team_key === selectedYahooTeamKey);
            const syncedTeam = await syncYahooTeam({
                yahooLeagueKey: selectedYahooLeagueKey,
                yahooTeamKey: selectedYahooTeamKey,
                leagueName: leagueData?.name,
                teamName: teamData?.name
            });
            console.log("Synced Yahoo Team:", syncedTeam);
            setYahooSuccess(`Team "${syncedTeam?.team_name || teamData?.name || 'Selected Team'}" synced successfully!`);
            // Reset Yahoo form state
            setYahooLeagues([]);
            setSelectedYahooLeagueKey('');
            setYahooTeamsInLeague([]);
            setSelectedYahooTeamKey('');
            setYahooAuthAttempted(false); // Allow re-fetching leagues later if needed

        } catch (error) {
            console.error("Sync Yahoo team failed:", error);
            const errorMessage = error.response?.data?.detail || error.message || "Failed to sync Yahoo team.";
            if (error.response?.status === 401 || error.response?.status === 409 || error.response?.status === 400) {
                setYahooError(errorMessage);
            } else {
                 setYahooError("An unexpected error occurred during Yahoo sync.");
            }
        } finally {
            setIsLoadingYahooSync(false);
        }
    };


    // --- JSX Rendering with Improved Styling ---
    return (
        <div className="container mx-auto p-4 md:p-8 bg-base-200 min-h-screen"> {/* Added bg and min-h-screen */}
            <h1 className="text-3xl md:text-4xl font-bold mb-8 text-center font-style: italic text-white">Add Your Fantasy Team</h1> {/* Enhanced heading */}

            {/* Mode Selection Tabs */}
            <div className="tabs tabs-boxed mb-8 justify-center bg-base-100 shadow-md"> {/* Added bg and shadow */}
                <a
                    className={`tab tab-lg md:tab-md flex-grow sm:flex-grow-0 ${mode === 'manual' ? 'tab-active font-semibold' : ''}`} // Responsive tabs, bold active
                    onClick={() => {
                        setMode('manual');
                        // Optional: Clear Sleeper state when switching to Manual
                        setSleeperUsername(''); setSleeperLeagues([]); setSelectedSleeperLeagueId('');
                        setSleeperUserRosters([]); setSelectedSleeperRosterId('');
                        setIsLoadingSleeperLeagues(false); setIsLoadingSleeperRosters(false); setIsLoadingSync(false);
                        setSleeperError(null); setSleeperSuccess(null);
                    }}
                >
                    Add Manually
                </a>
                <a
                    className={`tab tab-lg md:tab-md flex-grow sm:flex-grow-0 ${mode === 'sleeper' ? 'tab-active font-semibold' : ''}`} // Responsive tabs, bold active
                    onClick={() => {
                        setMode('sleeper');
                         // Optional: Clear Manual state when switching to Sleeper
                        setLeagueName(''); setTeamName(''); setSearchTerm(''); setSearchResults([]); setSelectedPlayers([]);
                        setIsLoadingSearch(false); setIsLoadingSave(false);
                        setManualError(null); setManualSuccess(null);
                    }}
                >
                    Sync with Sleeper
                </a>
                <a
                    className={`tab tab-lg md:tab-md flex-grow sm:flex-grow-0 ${mode === 'yahoo' ? 'tab-active font-semibold' : ''}`}
                    onClick={() => setMode('yahoo')}
                >
                    Sync with Yahoo
                </a>
            </div>

            {/* --- Manual Mode Form --- */}
            {mode === 'manual' && (
                <div className="card bg-base-100 shadow-xl p-6 md:p-8 max-w-2xl mx-auto border border-gray-300"> {/* Added border */}
                    <h2 className="text-2xl font-semibold mb-6 text-center text-accent">Manual Team Entry</h2> {/* Enhanced subheading */}

                    {/* Error and Success Messages */}
                    {manualError && (
                        <div role="alert" className="alert alert-error mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <span>{manualError}</span>
                        </div>
                    )}
                    {manualSuccess && (
                        <div role="alert" className="alert alert-success mb-4">
                             <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                             <span>{manualSuccess}</span>
                        </div>
                    )}

                    {/* Form Inputs */}
                    <div className="form-control mb-4">
                        <label className="label"><span className="label-text font-medium">League Name</span></label> {/* Added font-medium */}
                        <input
                            type="text"
                            placeholder="E.g., My Home League"
                            className="input input-bordered w-full" // Ensure full width
                            value={leagueName}
                            onChange={e => setLeagueName(e.target.value)}
                        />
                    </div>

                    <div className="form-control mb-4">
                        <label className="label"><span className="label-text font-medium">Your Team Name</span></label> {/* Added font-medium */}
                        <input
                            type="text"
                            placeholder="E.g., The Gridiron Greats"
                            className="input input-bordered w-full" // Ensure full width
                            value={teamName}
                            onChange={e => setTeamName(e.target.value)}
                        />
                    </div>

                    <div className="form-control mb-4">
                        <label className="label"><span className="label-text font-medium">Add Players by Name</span></label> {/* Added font-medium */}
                        <div className="flex items-center"> {/* Flex container for input and loading spinner */}
                           <input
                                type="text"
                                placeholder="Search for player name..."
                                className="input input-bordered w-full"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                           />
                           {isLoadingSearch && <span className="loading loading-spinner loading-sm ml-3 text-primary"></span>} {/* Adjusted loading spinner size and color */}
                        </div>
                    </div>

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                        <ul className="menu bg-base-200 rounded-box mb-4 max-h-48 overflow-y-auto shadow-inner"> {/* Added shadow-inner */}
                            {searchResults.map(player => (
                                <li key={player.id}>
                                    {/* Added role="button" for semantics, improved padding/hover */}
                                    <a onClick={() => addPlayer(player)} className="py-2 px-4 hover:bg-base-300 cursor-pointer">
                                        {player.name} ({player.position} - {player.team})
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Selected Players Display */}
                    <div className="mb-6 border-t border-b border-gray-200 py-4"> {/* Added borders and padding */}
                        <label className="label"><span className="label-text font-medium">Selected Roster ({selectedPlayers.length})</span></label> {/* Added font-medium */}
                        {selectedPlayers.length === 0 && <p className="text-sm italic text-base-content/60">No players added yet.</p>}
                        <div className="flex flex-wrap gap-2 mt-2"> {/* Added top margin */}
                            {selectedPlayers.map(player => (
                                <PlayerBadge key={player.id} player={player} onRemove={removePlayer} />
                            ))}
                        </div>
                    </div>

                    {/* Save Button */}
                    <button
                        className="btn btn-primary w-full btn-lg" // Made button larger
                        onClick={handleSaveManualTeam}
                        disabled={isLoadingSave || !leagueName.trim() || !teamName.trim() || selectedPlayers.length === 0} // Disable button based on validation and loading
                    >
                        {isLoadingSave ? <span className="loading loading-spinner"></span> : 'Save Manual Team'}
                    </button>
                </div>
            )}

            {/* --- Sleeper Mode Form --- */}
            {mode === 'sleeper' && (
                <div className="card bg-base-100 shadow-xl p-6 md:p-8 max-w-2xl mx-auto border border-gray-300"> {/* Added border */}
                    <h2 className="text-2xl font-semibold mb-6 text-center text-accent">Sync with Sleeper</h2> {/* Enhanced subheading */}

                    {/* Error and Success Messages */}
                    {sleeperError && (
                        <div role="alert" className="alert alert-error mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <span>{sleeperError}</span>
                        </div>
                    )}
                     {sleeperSuccess && (
                        <div role="alert" className="alert alert-success mb-4">
                             <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                             <span>{sleeperSuccess}</span>
                        </div>
                    )}

                    {/* Username input and Find Leagues Button */}
                    <div className="form-control mb-4">
                        <label className="label"><span className="label-text font-medium">Sleeper Username</span></label> {/* Added font-medium */}
                        <div className="flex gap-2"> {/* Use flex and gap for alignment */}
                            <input
                                type="text"
                                placeholder="Your Sleeper Username"
                                className="input input-bordered flex-grow" // Allow input to grow
                                value={sleeperUsername}
                                onChange={e => setSleeperUsername(e.target.value)}
                            />
                            <button
                                className="btn btn-secondary"
                                onClick={handleFetchSleeperLeagues}
                                disabled={isLoadingSleeperLeagues || !sleeperUsername.trim()} // Disable button while loading or if username is empty
                            >
                                {isLoadingSleeperLeagues ? <span className="loading loading-spinner loading-xs"></span> : 'Find Leagues'}
                            </button>
                        </div>
                        {/* Alt text for instruction */}
                            <label className="label pt-1 pb-0 w-full"> {/* Ensure label takes full width, adjust padding */}
                                <span className="label-text-alt text-xs italic text-base-content/70 w-full block whitespace-normal break-words">
                                    {/* Added w-full, block, whitespace-normal, break-words */}
                                    Only public league data for the current season ({new Date().getFullYear()}) will be checked.
                                </span>
                            </label> 
                         </div>

                    {/* League Select */}
                    {sleeperLeagues.length > 0 && (
                        <div className="form-control mb-4">
                            <label className="label"><span className="label-text font-medium">Select League</span></label> {/* Added font-medium */}
                            <select
                                className="select select-bordered w-full" // Ensure full width
                                value={selectedSleeperLeagueId}
                                onChange={e => setSelectedSleeperLeagueId(e.target.value)}
                                disabled={isLoadingSleeperRosters} // Disable during roster fetch
                            >
                                <option value="" disabled>-- Choose a league --</option>
                                {sleeperLeagues.map(league => (
                                    <option key={league.league_id} value={league.league_id}>
                                        {league.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Roster Select / Display */}
                    {isLoadingSleeperRosters && <div className="text-center my-4"><span className="loading loading-dots loading-md text-primary"></span></div>} {/* Adjusted loading spinner color */}

                    {/* Display select for multiple rosters or message for single/none */}
                    {selectedSleeperLeagueId && !isLoadingSleeperRosters && (
                         sleeperUserRosters.length > 1 ? (
                            <div className="form-control mb-4">
                                <label className="label"><span className="label-text font-medium">Select Your Team</span></label> {/* Added font-medium */}
                                <select
                                    className="select select-bordered w-full" // Ensure full width
                                    value={selectedSleeperRosterId}
                                    onChange={e => setSelectedSleeperRosterId(e.target.value)}
                                >
                                    <option value="" disabled>-- Choose your team --</option>
                                    {sleeperUserRosters.map(roster => (
                                        <option key={roster.roster_id} value={roster.roster_id}>
                                            {roster.team_name || `Roster ID: ${roster.roster_id}`} {/* Display team name if available */}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : sleeperUserRosters.length === 1 ? (
                            <p className="mb-4 text-sm italic text-base-content/70">Detected team: "{sleeperUserRosters[0].team_name}"</p> // Added text color
                        ) : (
                             selectedSleeperLeagueId && !isLoadingSleeperLeagues && !isLoadingSleeperRosters && !sleeperError && ( // Only show if a league is selected, not loading, and no error
                                <p className="mb-4 text-sm italic text-base-content/70">No team found for this username in the selected league.</p> // Added text color
                             )
                        )
                    )}


                    {/* Sync Button */}
                    {selectedSleeperLeagueId && selectedSleeperRosterId && !isLoadingSleeperRosters && (
                        <button
                            className="btn btn-primary w-full btn-lg mt-4" // Made button larger
                            onClick={handleSyncSleeperTeam}
                            disabled={isLoadingSync} // Disable while syncing
                        >
                            {isLoadingSync ? <span className="loading loading-spinner"></span> : 'Sync Selected Sleeper Team'}
                        </button>
                    )}
                </div>
            )}

            {/* --- NEW: Yahoo Mode Form --- */} 
            {mode === 'yahoo' && (
                 <div className="card bg-base-100 shadow-xl p-6 md:p-8 max-w-2xl mx-auto border border-gray-300">
                    <h2 className="text-2xl font-semibold mb-6 text-center text-accent">Sync with Yahoo</h2>
                    
                     {/* Error and Success Messages */} 
                     {yahooError && (
                        <div role="alert" className="alert alert-error mb-4">
                             <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <span>{yahooError}</span>
                         </div>
                    )}
                     {yahooSuccess && (
                        <div role="alert" className="alert alert-success mb-4">
                             <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                             <span>{yahooSuccess}</span>
                        </div>
                    )}

                     {/* Yahoo Login/Fetch Button */} 
                     <div className="form-control mb-4 text-center"> {/* Centered button */} 
                         {/* Show login button initially or if auth failed */} 
                        {(!yahooAuthAttempted || yahooError?.includes("authentication failed")) && (
                            <button 
                                className="btn btn-info btn-outline" 
                                onClick={redirectToYahooLogin} 
                                disabled={isLoadingYahooLeagues} // Prevent double-click
                            >
                                Connect to Yahoo & Fetch Leagues
                            </button>
                        )}
                         {/* Show refresh button if auth was attempted but maybe no leagues were found initially */} 
                        {(yahooAuthAttempted && !yahooError?.includes("authentication failed")) && (
                             <button 
                                className="btn btn-secondary btn-outline" 
                                onClick={handleFetchYahooLeagues} 
                                disabled={isLoadingYahooLeagues}
                            >
                                {isLoadingYahooLeagues ? <span className="loading loading-spinner loading-xs"></span> : 'Refresh Yahoo Leagues'}
                            </button>
                        )}
                    </div>

                     {/* Loading Indicator for Leagues */} 
                    {isLoadingYahooLeagues && <div className="text-center my-4"><span className="loading loading-dots loading-md text-primary"></span></div>}

                    {/* Yahoo League Select */} 
                    {!isLoadingYahooLeagues && yahooLeagues.length > 0 && (
                         <div className="form-control mb-4">
                            <label className="label"><span className="label-text font-medium">Select Yahoo League ({new Date().getFullYear()})</span></label>
                            <select
                                className="select select-bordered w-full"
                                value={selectedYahooLeagueKey}
                                onChange={e => setSelectedYahooLeagueKey(e.target.value)}
                                disabled={isLoadingYahooTeams} // Disable while fetching teams
                            >
                                <option value="" disabled>-- Choose a league --</option>
                                {yahooLeagues.map(league => (
                                    <option key={league.league_key} value={league.league_key}>
                                        {league.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Loading Indicator for Teams */} 
                    {isLoadingYahooTeams && <div className="text-center my-4"><span className="loading loading-dots loading-md text-primary"></span></div>}

                    {/* Yahoo Team Select */} 
                    {selectedYahooLeagueKey && !isLoadingYahooTeams && (
                        yahooTeamsInLeague.length > 1 ? (
                             <div className="form-control mb-4">
                                <label className="label"><span className="label-text font-medium">Select Your Yahoo Team</span></label>
                                <select
                                    className="select select-bordered w-full"
                                    value={selectedYahooTeamKey}
                                    onChange={e => setSelectedYahooTeamKey(e.target.value)}
                                    disabled={isLoadingYahooSync}
                                >
                                    <option value="" disabled>-- Choose your team --</option>
                                    {yahooTeamsInLeague.map(team => (
                                        <option key={team.team_key} value={team.team_key}>
                                            {team.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                         ) : yahooTeamsInLeague.length === 1 ? (
                            <p className="mb-4 text-sm italic text-base-content/70">Detected team: "{yahooTeamsInLeague[0].name}"</p>
                        ) : (
                             selectedYahooLeagueKey && !yahooError && (
                                 <p className="mb-4 text-sm italic text-base-content/70">No team found for you in this league.</p>
                            )
                        )
                    )}

                     {/* Yahoo Sync Button */} 
                    {selectedYahooLeagueKey && selectedYahooTeamKey && !isLoadingYahooTeams && (
                        <button
                            className="btn btn-primary w-full btn-lg mt-4"
                            onClick={handleSyncYahooTeam}
                            disabled={isLoadingYahooSync}
                        >
                            {isLoadingYahooSync ? <span className="loading loading-spinner"></span> : 'Sync Selected Yahoo Team'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default AddTeamPage;